<template>
    <div class="input-tag-checker">
        <div v-if="err" class="err-msg">{{ err }}</div>
        <div v-if="sqlerr" class="err-msg sql">{{ sqlerr }}</div>

        <div class="translate-msg">Requête SQL</div>
        <div class="input--checker">
            <TextArea
                class="stretch"
                placeholder="'tag1'='*' AND ('tag2'='valeur' OR 'tag3'!='valeur')"
                required
                v-model="translatedQuery"
                @input="handleSQLInput"
            />
            <font-awesome-icon
                v-if="!disabled && !sqlerr"
                icon="check-circle"
                class="fa-lg status green"
            />
            <font-awesome-icon
                v-if="!disabled && sqlerr"
                icon="times-circle"
                class="fa-lg status red"
            />
        </div>

        <div class="translate-msg">Expression JSON</div>
        <div class="input--checker">
            <TextArea
                class="stretch"
                placeholder="Expression JSON"
                required
                v-model="query"
                @input="handleJSONInput"
            />
            <font-awesome-icon
                v-if="!disabled && !err"
                icon="check-circle"
                class="fa-lg status green"
            />
            <font-awesome-icon
                v-if="!disabled && err"
                icon="times-circle"
                class="fa-lg status red"
            />
        </div>
    </div>
</template>

<script>
import TextArea from '@/components/TextArea';
import sqlParser from 'js-sql-parser';

export default {
    props: {
        value: {
            type: Object,
        },
    },
    data() {
        return {
            err: null,
            sqlerr: null,
            disabled: false,
            inputQuery: '',
            translatedQuery: '',
            lastSQLUpdate: 0,
        };
    },
    computed: {
        query: {
            get() {
                if (this.value) {
                    const string = JSON.stringify(this.value, null, 2);
                    if (string && string !== '{}') {
                        // Lance les tests
                        this.testValue(string);
                        return string;
                    }
                }
                return '';
            },
            set() {},
        },
    },
    methods: {
        /**
         * Lancé lors de la modification du JSON
         */
        handleJSONInput(value) {
            // Expression traduite en SQL
            this.translatedQuery = '';
            // Lance les tests
            this.testValue(value);
            if (!this.err) {
                // Emit événement changement
                this.$emit('input', JSON.parse(value));
            }
        },
        /**
         * Lancé lors de la modification du SQL
         */
        handleSQLInput(value) {
            this.sqlerr = null;
            try {
                const parsedSQL = sqlParser.parse(`SELECT * FROM tmp WHERE ${value}`);

                if (parsedSQL) {
                    const JSONEquivalent = this.translateSQL_(parsedSQL);

                    if (JSONEquivalent) {
                        this.lastSQLUpdate = Date.now();

                        // Emit événement changement
                        this.$emit('input', JSONEquivalent);
                    }
                }
            } catch (error) {
                this.sqlerr = 'SQL invalide';
                this.lastSQLUpdate = Date.now();
                this.$emit('input', {});
            }
        },
        /**
         * Teste la valeur et remplit this.err ainsi que this.translatedQuery
         */
        testValue(value) {
            this.err = null;
            try {
                // Requête format objet
                const parsedQuery = JSON.parse(value);
                // Premier opérateur ([op.or] ou [op.and])
                const firstOp = this.getFirstOperator_(parsedQuery);
                if (!firstOp || !parsedQuery[firstOp]) {
                    this.err = 'Opérateur manquant [op.or] ou [op.and]';
                    return false;
                }
                if (!Array.isArray(parsedQuery[firstOp])) {
                    this.err = 'Opérateur doit contenir un tableau de relations';
                    return false;
                }
                for (let i = 0; i < parsedQuery[firstOp].length; i++) {
                    const node = parsedQuery[firstOp][i];
                    if (Object.keys(node).length !== 1) {
                        this.err = 'Il faut un attribut par relation';
                        return false;
                    }
                }
                // Met à jour la version SQL
                if (this.lastSQLUpdate + 100 < Date.now()) {
                    this.translatedQuery = this.translateQuery_(parsedQuery);
                }
            } catch (error) {
                this.err = 'JSON invalide';
                return false;
            }
            return true;
        },
        /**
         * Retourne le premier opérateur présent
         */
        getFirstOperator_(parsedQuery) {
            if (parsedQuery['[op.or]']) {
                return '[op.or]';
            } else if (parsedQuery['[op.and]']) {
                return '[op.and]';
            } else {
                return false;
            }
        },
        /**
         * Traduit la requête et retourne son équivalent SQL
         */
        translateQuery_(parsedQuery) {
            let query = '';
            const firstOp = this.getFirstOperator_(parsedQuery);
            const sqlOp = firstOp === '[op.or]' ? 'OR' : firstOp === '[op.and]' ? 'AND' : null;

            if (Array.isArray(parsedQuery[firstOp]) && sqlOp !== null) {
                for (let i = 0; i < parsedQuery[firstOp].length; i++) {
                    const node = parsedQuery[firstOp][i];
                    const key = Object.keys(node)[0];
                    const val = node[key];

                    if (i > 0) {
                        query += ` ${sqlOp} `;
                    }

                    // Gère différents opérateurs :
                    // =
                    // != : op.ne
                    // >  : op.gt
                    // >= : op.gte
                    // <  : op.lt
                    // <= : op.lte
                    if (typeof val === 'string') {
                        query += `'${key}'='${val}'`;
                    } else if (typeof val === 'object') {
                        if ('[op.ne]' in val) {
                            const opVal = val['[op.ne]'];
                            query += `'${key}'!='${opVal}'`;
                        } else if ('[op.gt]' in val) {
                            const opVal = val['[op.gt]'];
                            query += `'${key}'>'${opVal}'`;
                        } else if ('[op.gte]' in val) {
                            const opVal = val['[op.gte]'];
                            query += `'${key}'>='${opVal}'`;
                        } else if ('[op.lt]' in val) {
                            const opVal = val['[op.lt]'];
                            query += `'${key}'<'${opVal}'`;
                        } else if ('[op.lte]' in val) {
                            const opVal = val['[op.lte]'];
                            query += `'${key}'<='${opVal}'`;
                        } else {
                            query += `(${this.translateQuery_(node)})`;
                        }
                    }
                }
            }

            return query;
        },
        /**
         * Transforme le SQL en JSON
         */
        translateSQL_(parsedSQL) {
            if (parsedSQL.value && parsedSQL.value.where) {
                const JSONEquivalent = this.recursiveTranslateSQL_(parsedSQL.value.where, true);
                return JSONEquivalent;
            }
        },
        /**
         * Traduit une chaîne SQL en JSON
         *
         * Exemples de chaînes SQL :
         * "col1"='val1'
         * "col1"='val1' or "col2"='val2' or "col3"='val3' or "col4"='val4'
         * "col1"='val1' and "col2"='val2' and "col3"='val3' and "col4"='val4'
         * ("col1"='val1' or "col2"='val2' or "col3"='val3' or "col4"='val4')
         * ("col1"='val1' or "col2"='val2' or "col3"='val3' or "col4"='val4') and ("col5"='val5' and "col6"='val6')
         */
        recursiveTranslateSQL_(parsedSQL, firstOp) {
            firstOp = firstOp ? firstOp : false;

            let result = {};

            if (parsedSQL && parsedSQL.type) {
                if (parsedSQL.type === 'ComparisonBooleanPrimary') {
                    const tuple = {};
                    const tupleRight = {};
                    const left = this.trimStringQuotes_(parsedSQL.left.value);
                    const right = this.trimStringQuotes_(parsedSQL.right.value);
                    const ope = this.trimStringQuotes_(parsedSQL.operator);

                    // Gère différents opérateurs :
                    // =
                    // != : op.ne
                    // >  : op.gt
                    // >= : op.gte
                    // <  : op.lt
                    // <= : op.lte
                    if (ope === '=') {
                        tuple[left] = right;
                    } else if (ope === '!=') {
                        tupleRight['[op.ne]'] = right;
                        tuple[left] = tupleRight;
                    } else if (ope === '>') {
                        tupleRight['[op.gt]'] = right;
                        tuple[left] = tupleRight;
                    } else if (ope === '>=') {
                        tupleRight['[op.gte]'] = right;
                        tuple[left] = tupleRight;
                    } else if (ope === '<') {
                        tupleRight['[op.lt]'] = right;
                        tuple[left] = tupleRight;
                    } else if (ope === '<=') {
                        tupleRight['[op.lte]'] = right;
                        tuple[left] = tupleRight;
                    }

                    if (firstOp) {
                        result['[op.and]'] = [tuple];
                    } else {
                        result = tuple;
                    }
                } else if (parsedSQL.type === 'OrExpression') {
                    result['[op.or]'] = [];
                    const left = this.recursiveTranslateSQL_(parsedSQL.left, false);
                    const right = this.recursiveTranslateSQL_(parsedSQL.right, false);

                    // Permet de faire de la simplification
                    if (Object.keys(left).length === 1 && Object.keys(left)[0] === '[op.or]') {
                        for (let i = 0; i < left['[op.or]'].length; i++) {
                            result['[op.or]'].push(left['[op.or]'][i]);
                        }
                    } else {
                        result['[op.or]'].push(left);
                    }

                    result['[op.or]'].push(right);
                } else if (parsedSQL.type === 'AndExpression') {
                    result['[op.and]'] = [];
                    const left = this.recursiveTranslateSQL_(parsedSQL.left, false);
                    const right = this.recursiveTranslateSQL_(parsedSQL.right, false);

                    // Permet de faire de la simplification
                    if (Object.keys(left).length === 1 && Object.keys(left)[0] === '[op.and]') {
                        for (let i = 0; i < left['[op.and]'].length; i++) {
                            result['[op.and]'].push(left['[op.and]'][i]);
                        }
                    } else {
                        result['[op.and]'].push(left);
                    }

                    result['[op.and]'].push(right);
                } else if (parsedSQL.type === 'SimpleExprParentheses') {
                    result = this.recursiveTranslateSQL_(parsedSQL.value, false);
                } else if (parsedSQL.type === 'ExpressionList') {
                    if (parsedSQL.value.length === 1) {
                        result = this.recursiveTranslateSQL_(parsedSQL.value[0], false);
                    } else {
                        result['[op.and]'] = [];
                        for (let i = 0; i < parsedSQL.value.length; i++) {
                            result['[op.and]'].push(
                                this.recursiveTranslateSQL_(parsedSQL.value[i], false),
                            );
                        }
                    }
                } else {
                    console.error(`ParsedSQL type ${parsedSQL.type} non known`);
                }
            }
            return result;
        },
        /**
         * Enlève les quotes en début et fin de chaîne
         */
        trimStringQuotes_(str) {
            if (str.charAt(0) === '"' && str.charAt(str.length - 1) === '"') {
                return str.substr(1, str.length - 2);
            }
            if (str.charAt(0) === "'" && str.charAt(str.length - 1) === "'") {
                return str.substr(1, str.length - 2);
            }
            return str;
        },
    },
    components: {
        TextArea,
    },
};
</script>

<style lang="scss">
.input-tag-checker {
    position: relative;

    .translate-msg {
        padding-top: 10px;
    }
    .input--checker {
        position: relative;

        input {
            padding-right: 33px;
            text-overflow: ellipsis;
        }

        .status {
            position: absolute;
            top: -20px;
            right: 10px;

            &.green {
                color: #93c11f;
            }

            &.red {
                color: #ea5b0c;
            }
        }
    }

    .err-msg {
        position: absolute;
        right: 30px;
        color: #ea5b0c;
        font-weight: 700;
        top: -20px;
    }

    .sql {
        top: -5px;
    }
}
</style>
